import React, { FunctionComponent, useContext, ChangeEvent } from "react";
import { Routes } from "../../app";
import { Choice, Alert } from "@cmsgov/ds-medicare-gov";
import { CoverageType } from "./types";
import { useTranslate } from "../../helpers";
import { FormattedMessage } from "react-intl-phraseapp";
import { Continue } from "../../components";
import { AppContext } from "../../app/store";

interface PlanQuestionsProps {
  handleChoice: (e: ChangeEvent<HTMLInputElement>) => void;
  coverageSelection: CoverageType;
  hideMedigapButton?: boolean;
}

export const coverageTypeIdMappings = {
  [CoverageType.MAPD]: "what-coverage-mapd",
  [CoverageType.PDP]: "what-coverage-pdp",
  [CoverageType.PDPAndMedigap]: "what-coverage-pdp-medigap",
  [CoverageType.MedigapPolicy]: "what-coverage-medigap",
};

const PlanQuestions: FunctionComponent<PlanQuestionsProps> = ({
  handleChoice,
  coverageSelection,
  hideMedigapButton,
}) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppContext);

  const mapdSelected = coverageSelection === CoverageType.MAPD;
  const pdpSelected = coverageSelection === CoverageType.PDP;
  const medigapSelected = coverageSelection === CoverageType.MedigapPolicy;
  const pdpMedigapSelected = coverageSelection === CoverageType.PDPAndMedigap;

  return (
    <>
      <Choice
        type="radio"
        name="what-coverage"
        id={coverageTypeIdMappings[CoverageType.MAPD]}
        value={CoverageType.MAPD}
        onChange={handleChoice}
        checked={mapdSelected}
        checkedChildren={
          <Alert
            role="alert"
            hideIcon={true}
            data-testid={coverageTypeIdMappings[CoverageType.MAPD]}
          >
            {t("landing_page.coverage_selection.mapd.text")}
          </Alert>
        }
      >
        {t("landing_page.coverage_selection.mapd.heading")}
      </Choice>

      <Choice
        type="radio"
        name="what-coverage"
        id={coverageTypeIdMappings[CoverageType.PDP]}
        value={CoverageType.PDP}
        onChange={handleChoice}
        checked={pdpSelected}
        checkedChildren={
          <Alert
            role="alert"
            hideIcon={true}
            data-testid={coverageTypeIdMappings[CoverageType.PDP]}
          >
            {t("landing_page.coverage_selection.pdp.text")}
          </Alert>
        }
      >
        <FormattedMessage
          id="landing_page.coverage_selection.pdp.heading"
          tagName="span"
          values={{
            message: <span aria-label="Dee">D</span>,
          }}
        />
      </Choice>

      <Choice
        type="radio"
        name="what-coverage"
        id={coverageTypeIdMappings[CoverageType.PDPAndMedigap]}
        value={CoverageType.PDPAndMedigap}
        onChange={handleChoice}
        checked={pdpMedigapSelected}
        checkedChildren={
          <Alert
            role="alert"
            hideIcon={true}
            data-testid={coverageTypeIdMappings[CoverageType.PDPAndMedigap]}
          >
            {t("landing_page.coverage_selection.pdp_medigap.text")}
          </Alert>
        }
      >
        <FormattedMessage
          id="landing_page.coverage_selection.pdp_medigap.heading"
          tagName="span"
          values={{
            message: <span aria-label="Dee">D</span>,
          }}
        />
      </Choice>

      <Choice
        type="radio"
        name="what-coverage"
        id={coverageTypeIdMappings[CoverageType.MedigapPolicy]}
        value={CoverageType.MedigapPolicy}
        onChange={handleChoice}
        checked={medigapSelected}
        checkedChildren={
          <>
            <Alert
              role="alert"
              hideIcon={true}
              data-testid={coverageTypeIdMappings[CoverageType.MedigapPolicy]}
            >
              {t("landing_page.coverage_selection.medigap.text")}
            </Alert>
            {!hideMedigapButton && (
              <>
                <Continue
                  useLink
                  to={`#${Routes.medigap.landingPage}?lang=${language}`}
                >
                  {t("landing_page.coverage_selection.view_policies")}
                </Continue>
              </>
            )}
          </>
        }
      >
        {t("landing_page.coverage_selection.medigap.heading")}
      </Choice>
    </>
  );
};

export default PlanQuestions;
